// Server-side backup of a guest's progress, keyed by the browser's device id.
//
// Guests already keep everything in localStorage, which is what makes the site
// usable without an account. This module adds the other half: a copy on the
// server, so the progress survives things localStorage alone does not — iOS
// evicting script storage after a week of not visiting, a browser update that
// resets the origin, or the student clearing site data but keeping cookies.
//
// It is a backup, not a login. Anyone holding the device id can read that
// device's progress, so nothing account-shaped (token, username, password) is
// ever in the payload, and guests never appear on the leaderboard.

import { getDeviceId, deviceLabel } from './device';
import { AUTH_BASE } from './api';

const GUEST_API = `${AUTH_BASE}/api/guest`;

// The exact localStorage keys that count as "progress".
//
// An allowlist, not a dump of the whole store, for two reasons: `authToken`
// must never leave this browser, and a blanket copy would ship whatever a
// future feature happens to park in localStorage straight to the server
// without anyone deciding it should be there.
export const GUEST_KEYS = [
  // gamification core
  'sv_game_state',        // xp, coins, streak, badges, shields, avatar
  'sv_quests',            // today's three quests + chest
  'sv_interests',
  'sv_mistakes',          // the mistake deck
  // per-activity progress
  'sv_math_mastered',
  'sv_mathpath',
  'arenaBest',
  'chessProgress',
  'singingProgress',
  'yogaProgress',
  'yogaGameLeaderboard',
  'labDone',
  'wonderRead',
  'sudokuMode',
  // settings the student chose, which are just as annoying to redo
  'myGrade',
  'onboarded',
  'siteLanguage',
  'bigText',
  'sfxOn',
  'userBio',
  'chessPlayerName'
];

// Matches the server's cap. Deliberately well under express.json()'s default
// 100kb body limit: these values are themselves JSON, so escaping every quote
// roughly doubles them on the wire. `sv_mistakes` is the only key that grows
// much (40 full question objects) and lands around 12KB at worst.
const MAX_VALUE_BYTES = 16000;
const MAX_TOTAL_BYTES = 32 * 1024;

// A write every few seconds is plenty — the point is surviving a closed tab,
// not real-time replication.
const DEBOUNCE_MS = 2500;
const POLL_MS = 25000;

let started = false;
let timer = null;
let poller = null;
let lastSent = '';

function isGuestNow() {
  try { return !localStorage.getItem('authToken'); } catch { return false; }
}

function snapshot() {
  const out = {};
  let total = 0;
  for (const k of GUEST_KEYS) {
    let v = null;
    try { v = localStorage.getItem(k); } catch { return out; }
    if (typeof v !== 'string' || v.length > MAX_VALUE_BYTES) continue;
    if (total + v.length > MAX_TOTAL_BYTES) continue;
    total += v.length;
    out[k] = v;
  }
  return out;
}

// XP is the one field both sides compare on, so read it the same way in both
// places: out of the game-state blob, defaulting to 0 when it is missing or
// unparseable (a fresh browser).
export function xpOf(state) {
  try {
    const g = JSON.parse((state && state.sv_game_state) || '{}');
    return Number(g.xp) || 0;
  } catch { return 0; }
}

function restore(state) {
  let n = 0;
  for (const k of GUEST_KEYS) {
    const v = state[k];
    if (typeof v !== 'string') continue;
    try { localStorage.setItem(k, v); n++; } catch { /* out of quota */ }
  }
  return n;
}

/**
 * Fetch this device's stored progress and put it back if the server copy is
 * ahead of what this browser holds.
 *
 * "Ahead" is measured in XP, which only ever grows. On a normal return visit
 * localStorage is intact and the two agree, so nothing is written. The case
 * this exists for is a browser that lost its localStorage but kept the cookie:
 * local XP reads 0, the server still has the real total, and the student gets
 * their streak back instead of a blank slate.
 *
 * Returns true when localStorage was rewritten, so the caller knows the
 * in-memory reactive state it built at import time is now stale.
 */
export async function hydrateGuest() {
  if (!isGuestNow()) return false;
  const did = getDeviceId();
  let remote = null;
  try {
    const res = await fetch(`${GUEST_API}/state?did=${encodeURIComponent(did)}`);
    if (res.ok) {
      const body = await res.json();
      if (body && body.state && typeof body.state === 'object') remote = body.state;
    }
  } catch {
    // Offline. The local copy is authoritative anyway; just start syncing so
    // the first successful write catches the server up.
  }

  let restored = false;
  if (remote && xpOf(remote) > xpOf(snapshot())) {
    restored = restore(remote) > 0;
  }

  // Seed the change detector AFTER any restore, so a straight round trip does
  // not immediately POST back what we just pulled down.
  lastSent = JSON.stringify(snapshot());
  startGuestSync();
  return restored;
}

/** Push now, unless nothing has changed since the last write. */
async function flush() {
  if (!isGuestNow()) return;
  const state = snapshot();
  const json = JSON.stringify(state);
  if (json === lastSent) return;
  lastSent = json;
  try {
    await fetch(`${GUEST_API}/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ did: getDeviceId(), label: deviceLabel(), state })
    });
  } catch {
    // Offline — let the next change try again rather than losing the write.
    lastSent = '';
  }
}

/** Debounced push: a burst of awards sends one write with the final totals. */
export function queueGuestSync() {
  if (!isGuestNow()) return;
  clearTimeout(timer);
  timer = setTimeout(flush, DEBOUNCE_MS);
}

function onHidden() {
  // The tab may never come back. fetch() from a hiding page can be cancelled,
  // so hand the write to the browser with sendBeacon, which survives unload.
  if (!isGuestNow()) return;
  const state = snapshot();
  const json = JSON.stringify(state);
  if (json === lastSent) return;
  lastSent = json;
  try {
    const blob = new Blob(
      [JSON.stringify({ did: getDeviceId(), label: deviceLabel(), state })],
      { type: 'application/json' }
    );
    if (!navigator.sendBeacon(`${GUEST_API}/sync`, blob)) lastSent = '';
  } catch { lastSent = ''; }
}

function onVisibility() {
  if (document.visibilityState === 'hidden') onHidden();
}

/**
 * Start watching for changes.
 *
 * Progress is written to localStorage from a dozen different views (arena
 * best, yoga, singing, chess, Wonder), most of which never call into the
 * gamification service. Rather than thread a sync call through every one of
 * them, poll the allowlist on a slow timer and write only when the serialized
 * snapshot actually differs.
 */
export function startGuestSync() {
  if (started || !isGuestNow()) return;
  started = true;
  poller = setInterval(() => { if (isGuestNow()) flush(); else stopGuestSync(); }, POLL_MS);
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHidden);
}

export function stopGuestSync() {
  started = false;
  clearTimeout(timer);
  clearInterval(poller);
  document.removeEventListener('visibilitychange', onVisibility);
  window.removeEventListener('pagehide', onHidden);
}

/**
 * Called once the guest has an account. The account's own sync owns their
 * progress from here, so the guest writer is shut down and the device row is
 * marked claimed — that stops the row being restored over a real account and
 * lets it be pruned.
 */
export function claimGuestDevice(token) {
  stopGuestSync();
  try {
    fetch(`${GUEST_API}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ did: getDeviceId() })
    }).catch(() => {});
  } catch { /* best effort */ }
}
